import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertCircle, Crown, Download, FileText, Loader2, Plus, Sparkles, Trash2, Upload,
} from "lucide-react";
import Layout from "@/components/Layout";
import { getApiBase } from "@/lib/httpClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import GerarDocumentoDialog, { rotuloDaMacro } from "@/components/GerarDocumentoDialog";
import { DADOS } from "../lib/larguras";

const DOCX = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export function textoDoLimite(bloco) {
  if (!bloco) return "";
  if (bloco.limite < 0) return "ilimitados";
  return `${bloco.usados} de ${bloco.limite}`;
}

export function atingiuLimite(bloco) {
  return Boolean(bloco) && bloco.limite >= 0 && bloco.usados >= bloco.limite;
}

function dataBR(iso) {
  if (!iso) return "";
  const data = new Date(iso);
  return Number.isNaN(data.getTime()) ? "" : data.toLocaleDateString("pt-BR");
}

// O download vem autenticado, então não dá para usar um <a href> puro: busca o
// arquivo com a sessão e entrega o blob ao navegador.
async function baixar(url, nomeArquivo) {
  const { data } = await axios.get(url, { withCredentials: true, responseType: "blob" });
  const endereco = URL.createObjectURL(new Blob([data], { type: DOCX }));
  const link = document.createElement("a");
  link.href = endereco;
  link.download = nomeArquivo;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(endereco);
}

function NovoModeloDialog({ aberto, onFechar, onCriado }) {
  const [nome, setNome] = useState("");
  const [descricao, setDescricao] = useState("");
  const [arquivo, setArquivo] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const campoArquivo = useRef(null);

  useEffect(() => {
    if (!aberto) { setNome(""); setDescricao(""); setArquivo(null); }
  }, [aberto]);

  const escolher = (evento) => {
    const escolhido = evento.target.files?.[0] || null;
    setArquivo(escolhido);
    // O nome do arquivo é o melhor palpite para o nome do modelo.
    if (escolhido && !nome) setNome(escolhido.name.replace(/\.docx$/i, ""));
  };

  const enviar = async () => {
    if (!arquivo) { toast.error("Escolha o arquivo .docx do modelo."); return; }
    setEnviando(true);
    try {
      const corpo = new FormData();
      corpo.append("arquivo", arquivo);
      corpo.append("nome", nome.trim() || arquivo.name.replace(/\.docx$/i, ""));
      corpo.append("descricao", descricao.trim());
      const { data } = await axios.post(`${getApiBase()}/documentos/modelos`, corpo, { withCredentials: true });
      toast.success("Modelo cadastrado.");
      onCriado(data);
      onFechar();
    } catch (erro) {
      const detalhe = erro?.response?.data?.detail;
      toast.error(detalhe?.message || detalhe || "Não foi possível cadastrar o modelo.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={aberto} onOpenChange={(estado) => !estado && onFechar()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Cabinet Grotesk" }}>Novo modelo</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div>
            <Label htmlFor="modelo-arquivo" className="text-[#335480]">Arquivo do Word</Label>
            <input
              id="modelo-arquivo"
              data-testid="modelo-arquivo"
              ref={campoArquivo}
              type="file"
              accept=".docx"
              onChange={escolher}
              className="mt-1.5 block w-full text-base text-[#335480] file:mr-3 file:h-10 file:rounded-md file:border file:border-[#003882] file:bg-white file:px-4 file:text-base file:font-medium file:text-[#003882] hover:file:bg-[#e5edf5]"
            />
            <p className="text-[#335480] text-sm mt-1.5">
              Escreva <code className="text-[#003882]">{"{{ cliente.nome }}"}</code> onde o dado deve entrar. Até 20 MB.
            </p>
          </div>

          <div>
            <Label htmlFor="modelo-nome" className="text-[#335480]">Nome do modelo</Label>
            <Input
              id="modelo-nome"
              data-testid="modelo-nome"
              value={nome}
              onChange={(evento) => setNome(evento.target.value)}
              placeholder="Procuração ad judicia"
              className="mt-1.5"
            />
          </div>

          <div>
            <Label htmlFor="modelo-descricao" className="text-[#335480]">Descrição (opcional)</Label>
            <Textarea
              id="modelo-descricao"
              data-testid="modelo-descricao"
              value={descricao}
              onChange={(evento) => setDescricao(evento.target.value)}
              rows={2}
              placeholder="Quando usar este modelo"
              className="mt-1.5"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={onFechar} className="border-[#D1DEEE] text-[#335480]">Cancelar</Button>
          <Button
            data-testid="salvar-modelo"
            onClick={enviar}
            disabled={enviando}
            className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold"
          >
            {enviando ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : null}
            {enviando ? "Enviando..." : "Cadastrar modelo"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function DocumentosPage() {
  const [modelos, setModelos] = useState([]);
  const [gerados, setGerados] = useState([]);
  const [limites, setLimites] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [novoAberto, setNovoAberto] = useState(false);
  const [modeloParaGerar, setModeloParaGerar] = useState(null);

  const carregar = useCallback(async () => {
    try {
      const [respModelos, respGerados] = await Promise.all([
        axios.get(`${getApiBase()}/documentos/modelos`, { withCredentials: true }),
        axios.get(`${getApiBase()}/documentos/gerados`, { withCredentials: true }),
      ]);
      setModelos(respModelos.data?.modelos || []);
      setLimites(respModelos.data?.limites || null);
      setGerados(respGerados.data?.documentos || []);
    } catch {
      toast.error("Não foi possível carregar seus documentos.");
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const excluirModelo = async (modelo) => {
    if (!window.confirm(`Excluir o modelo "${modelo.nome}"? Os documentos já gerados continuam disponíveis.`)) return;
    try {
      await axios.delete(`${getApiBase()}/documentos/modelos/${modelo.modelo_id}`, { withCredentials: true });
      toast.success("Modelo excluído.");
      carregar();
    } catch {
      toast.error("Não foi possível excluir o modelo.");
    }
  };

  const excluirGerado = async (documento) => {
    if (!window.confirm(`Excluir "${documento.nome}"? Isso não devolve cota de geração.`)) return;
    try {
      await axios.delete(`${getApiBase()}/documentos/gerados/${documento.documento_id}`, { withCredentials: true });
      setGerados((atual) => atual.filter((item) => item.documento_id !== documento.documento_id));
    } catch {
      toast.error("Não foi possível excluir o documento.");
    }
  };

  const semCotaDeModelo = atingiuLimite(limites?.modelos);
  const semCotaDeGeracao = atingiuLimite(limites?.gerados);

  return (
    <Layout>
      <div className={DADOS}>
        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
              Documentos
            </h1>
            <p className="text-[#335480] mt-1.5">
              Cadastre seus modelos do Word e gere documentos já preenchidos com os dados do caso.
            </p>
          </div>
          <Button
            data-testid="novo-modelo"
            onClick={() => setNovoAberto(true)}
            disabled={semCotaDeModelo}
            className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold"
          >
            <Plus size={16} strokeWidth={1.5} aria-hidden="true" /> Novo modelo
          </Button>
        </div>

        {limites?.storage_ok === false && (
          <div className="border border-[#D1DEEE] bg-[#F4F7FB] rounded-lg p-4 mb-6 flex items-start gap-3" data-testid="storage-indisponivel">
            <AlertCircle size={20} strokeWidth={1.5} className="text-[#003882] mt-0.5 flex-shrink-0" aria-hidden="true" />
            <p className="text-[#335480]">
              O armazenamento de documentos ainda não está configurado neste ambiente.
            </p>
          </div>
        )}

        {limites && (
          <div className="grid sm:grid-cols-2 gap-4 mb-8" data-testid="painel-limites">
            <div className="border border-[#D1DEEE] bg-white rounded-lg p-5">
              <p className="text-[#335480]">Modelos cadastrados</p>
              <p className="text-2xl font-bold text-[#001533] mt-1" style={{ fontFamily: "Cabinet Grotesk" }}>
                {textoDoLimite(limites.modelos)}
              </p>
            </div>
            <div className="border border-[#D1DEEE] bg-white rounded-lg p-5">
              <p className="text-[#335480]">Documentos gerados</p>
              <p className="text-2xl font-bold text-[#001533] mt-1" style={{ fontFamily: "Cabinet Grotesk" }}>
                {textoDoLimite(limites.gerados)}
              </p>
            </div>
          </div>
        )}

        {(semCotaDeModelo || semCotaDeGeracao) && (
          <div className="border border-[#003882]/20 bg-[#e5edf5] rounded-lg p-5 mb-8 flex flex-wrap items-center justify-between gap-4" data-testid="aviso-limite">
            <p className="text-[#001533]">
              {semCotaDeGeracao
                ? `Você usou as ${limites.gerados.limite} gerações do plano FREE.`
                : `Você usou os ${limites.modelos.limite} modelos do plano FREE.`}
            </p>
            <Link to="/plano">
              <Button data-testid="upgrade-documentos" className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold">
                <Crown size={16} strokeWidth={1.5} aria-hidden="true" /> Liberar no PRO
              </Button>
            </Link>
          </div>
        )}

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-[#001533] mb-4" style={{ fontFamily: "Cabinet Grotesk" }}>
            Meus modelos
          </h2>

          {carregando && <p className="text-[#335480]">Carregando...</p>}

          {!carregando && modelos.length === 0 && (
            <div className="border border-[#D1DEEE] bg-white rounded-lg p-10 text-center" data-testid="modelos-vazio">
              <FileText size={32} strokeWidth={1.5} className="text-[#6685AD] mx-auto mb-3" aria-hidden="true" />
              <p className="text-[#001533] font-medium">Nenhum modelo cadastrado ainda.</p>
              <p className="text-[#335480] mt-1.5 max-w-md mx-auto">
                Envie um .docx com <code className="text-[#003882]">{"{{ cliente.nome }}"}</code> no lugar dos
                dados variáveis e o TUTO preenche o resto.
              </p>
              <Button
                onClick={() => setNovoAberto(true)}
                className="mt-5 bg-[#003882] hover:bg-[#00285e] text-white font-semibold"
                data-testid="novo-modelo-vazio"
              >
                <Upload size={16} strokeWidth={1.5} aria-hidden="true" /> Enviar o primeiro modelo
              </Button>
            </div>
          )}

          <div className="space-y-3">
            {modelos.map((modelo) => (
              <article
                key={modelo.modelo_id}
                data-testid={`modelo-${modelo.modelo_id}`}
                className="border border-[#D1DEEE] bg-white rounded-lg p-5 flex flex-wrap items-start justify-between gap-4"
              >
                <div className="min-w-0 flex-1">
                  <h3 className="text-[#001533] font-medium text-lg">{modelo.nome}</h3>
                  {modelo.descricao && <p className="text-[#335480] mt-0.5">{modelo.descricao}</p>}
                  <p className="text-[#6685AD] text-sm mt-1.5">
                    {modelo.arquivo_nome} · cadastrado em {dataBR(modelo.created_at)}
                  </p>

                  {/* Macro sob uma raiz nossa que ninguém preenche sairia vazia
                      no documento assinado — o aviso é aqui, não lá. */}
                  {modelo.macros?.desconhecidas?.length > 0 && (
                    <p className="text-[#335480] text-sm mt-2 flex items-start gap-2" data-testid={`aviso-macro-${modelo.modelo_id}`}>
                      <AlertCircle size={16} strokeWidth={1.5} className="text-[#003882] mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <span>
                        O TUTO não conhece {modelo.macros.desconhecidas.join(", ")} — essas marcações sairão em branco.
                      </span>
                    </p>
                  )}

                  {modelo.macros?.automaticos?.length > 0 && (
                    <p className="text-[#335480] text-sm mt-2">
                      Preenche: {modelo.macros.automaticos.slice(0, 4).map(rotuloDaMacro).join(", ")}
                      {modelo.macros.automaticos.length > 4 ? ` e mais ${modelo.macros.automaticos.length - 4}` : ""}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    data-testid={`gerar-${modelo.modelo_id}`}
                    onClick={() => setModeloParaGerar(modelo)}
                    disabled={semCotaDeGeracao}
                    className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold"
                  >
                    <Sparkles size={16} strokeWidth={1.5} aria-hidden="true" /> Gerar
                  </Button>
                  <Button
                    variant="outline"
                    aria-label={`Baixar o modelo ${modelo.nome}`}
                    data-testid={`baixar-modelo-${modelo.modelo_id}`}
                    onClick={() => baixar(`${getApiBase()}/documentos/modelos/${modelo.modelo_id}/arquivo`, modelo.arquivo_nome || `${modelo.nome}.docx`)}
                    className="border-[#D1DEEE] text-[#335480] hover:text-[#003882]"
                  >
                    <Download size={16} strokeWidth={1.5} aria-hidden="true" />
                  </Button>
                  <Button
                    variant="outline"
                    aria-label={`Excluir o modelo ${modelo.nome}`}
                    data-testid={`excluir-modelo-${modelo.modelo_id}`}
                    onClick={() => excluirModelo(modelo)}
                    className="border-[#D1DEEE] text-[#335480] hover:text-[#003882]"
                  >
                    <Trash2 size={16} strokeWidth={1.5} aria-hidden="true" />
                  </Button>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-[#001533] mb-4" style={{ fontFamily: "Cabinet Grotesk" }}>
            Documentos gerados
          </h2>

          {!carregando && gerados.length === 0 && (
            <p className="text-[#335480] border border-[#D1DEEE] bg-white rounded-lg p-6" data-testid="gerados-vazio">
              Nada gerado ainda. Escolha um modelo acima e clique em Gerar.
            </p>
          )}

          <div className="space-y-3">
            {gerados.map((documento) => (
              <article
                key={documento.documento_id}
                data-testid={`gerado-${documento.documento_id}`}
                className="border border-[#D1DEEE] bg-white rounded-lg p-5 flex flex-wrap items-center justify-between gap-4"
              >
                <div className="min-w-0 flex-1">
                  <h3 className="text-[#001533] font-medium">{documento.nome}</h3>
                  <p className="text-[#335480] text-sm mt-0.5">
                    {[documento.modelo_nome, documento.processo_numero, dataBR(documento.created_at)]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    data-testid={`baixar-${documento.documento_id}`}
                    onClick={() => baixar(`${getApiBase()}/documentos/gerados/${documento.documento_id}/arquivo`, `${documento.nome}.docx`)}
                    variant="outline"
                    className="border-[#003882] text-[#003882] hover:bg-[#e5edf5] font-medium"
                  >
                    <Download size={16} strokeWidth={1.5} aria-hidden="true" /> Baixar
                  </Button>
                  <Button
                    variant="outline"
                    aria-label={`Excluir ${documento.nome}`}
                    data-testid={`excluir-gerado-${documento.documento_id}`}
                    onClick={() => excluirGerado(documento)}
                    className="border-[#D1DEEE] text-[#335480] hover:text-[#003882]"
                  >
                    <Trash2 size={16} strokeWidth={1.5} aria-hidden="true" />
                  </Button>
                </div>
              </article>
            ))}
          </div>
        </section>
      </div>

      <NovoModeloDialog aberto={novoAberto} onFechar={() => setNovoAberto(false)} onCriado={carregar} />
      <GerarDocumentoDialog
        modelo={modeloParaGerar}
        aberto={Boolean(modeloParaGerar)}
        onFechar={() => setModeloParaGerar(null)}
        onGerado={(resposta) => {
          if (resposta?.documento) setGerados((atual) => [resposta.documento, ...atual]);
          if (resposta?.limites) setLimites(resposta.limites);
        }}
      />
    </Layout>
  );
}
